import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/axios";

/* ── tiny FAQ accordion ── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="faq-item" onClick={() => setOpen(!open)}>
      <div className="faq-question">
        <span>{q}</span>
        <span className="faq-arrow">{open ? "▲" : "▼"}</span>
      </div>
      {open && <div className="faq-answer">{a}</div>}
    </div>
  );
}

export default function Landing() {
  const [plans, setPlans] = useState([]);
  const [navScrolled, setNavScrolled] = useState(false);

  useEffect(() => {
    api.get("/plans/public").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const handler = () => setNavScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const features = [
    { icon: "🏫", title: "Multi-School SaaS", desc: "Each school gets a fully isolated, secure workspace. One platform, hundreds of schools — zero data leakage between tenants." },
    { icon: "⏱️", title: "Time-Locked Marks Entry", desc: "Set exact open and close windows for marks entry. Panels auto-open and auto-close via a background scheduler — no manual intervention." },
    { icon: "🔒", title: "Secure Student Portal", desc: "Students and parents verify identity with admission number, roll number, mobile, and date of birth. No passwords, no confusion." },
    { icon: "📄", title: "Auto PDF Result Cards", desc: "Generate professional, printable result cards for individual students or bulk-print an entire class with a single click." },
    { icon: "📊", title: "Excel Export & Reports", desc: "Export complete class results to Excel for offline analysis, board submissions, or parent-teacher meetings." },
    { icon: "📈", title: "Performance Graphs", desc: "Visual bar charts and performance trends help students and teachers identify strengths and areas for improvement." },
    { icon: "🎓", title: "Promotion List Generator", desc: "Automatically compute pass/fail status and generate promotion lists with configurable minimum percentage thresholds." },
    { icon: "🔔", title: "Auto Credential Alerts", desc: "When a teacher is added, login credentials are auto-generated and sent via email and SMS instantly." },
    { icon: "💳", title: "Online Payment (Razorpay)", desc: "Schools pay directly through a secure Razorpay checkout. Subscriptions activate the moment payment is confirmed." },
  ];

  const userTypes = [
    {
      icon: "🏛️",
      title: "Super Admin",
      color: "#1E3A5F",
      points: ["Manage all schools on the platform", "Create & update subscription plans", "Monitor revenue and analytics", "Control result template library"],
    },
    {
      icon: "🧑‍💼",
      title: "School Admin",
      color: "#2563EB",
      points: ["Add classes, subjects, students, teachers", "Schedule and control marks-entry panels", "Publish or withhold results by class", "Export results to Excel or PDF"],
    },
    {
      icon: "👩‍🏫",
      title: "Teacher",
      color: "#7C3AED",
      points: ["See only assigned subjects and classes", "Enter marks while the panel is open", "View-only access to other classes", "Auto-receive credentials via SMS/email"],
    },
    {
      icon: "👨‍👩‍👧",
      title: "Students & Parents",
      color: "#059669",
      points: ["No login or password needed", "Verify with admission no. + roll no.", "View published result cards online", "Print result card directly from portal"],
    },
  ];

  const testimonials = [
    { name: "Fr. Thomas Mathew", role: "Principal, St. Joseph's Academy, Ranchi", quote: "VRM completely transformed how we manage examinations. Our teachers now enter marks on mobile during the panel window — no more paper registers or Excel gymnastics. Results reach parents the same day they're published." },
    { name: "Mrs. Sunita Sharma", role: "Academic Head, Delhi Public School, Jamshedpur", quote: "The time-locked marks panel is a game-changer. Nobody can enter late marks or tamper with results after closing. The parent portal is especially loved — they check results on WhatsApp links we share." },
    { name: "Mr. Rakesh Gupta", role: "Administrator, Saraswati Vidya Mandir, Bokaro", quote: "We moved from handwritten result sheets to automated PDF cards in one week. The pricing is fair for schools like ours. The Excel export feature saved us two full days of work every exam cycle." },
  ];

  const faqs = [
    { q: "How does the school registration work?", a: "Click 'Register Your School', fill in your school and admin details, choose a subscription plan, and pay online via Razorpay. Your account activates instantly after payment — no waiting for manual approval." },
    { q: "Can teachers enter marks from mobile phones?", a: "Yes. The platform is browser-based and works on any device — desktop, tablet, or mobile. Teachers simply log in during the marks-entry window and fill in the digital marksheet." },
    { q: "What happens when the marks-entry panel closes?", a: "The system automatically locks the marksheet. Teachers see a 'Panel Closed' message and cannot submit any marks. The school admin can grant individual exceptions for late entries if needed." },
    { q: "Is student data safe? Who can see it?", a: "Each school's data is fully isolated. No teacher or admin from another school can ever access your data. The student portal requires four-factor verification (admission no., roll no., mobile, and date of birth)." },
    { q: "Can I try the platform before paying?", a: "Visit the Student/Parent Portal at /portal to see the result lookup experience with no login. For a full demo of the admin panel, contact us and we will set up a trial environment." },
    { q: "What is included in each subscription plan?", a: "All plans include unlimited students, teachers, classes, and exam sessions. Higher plans unlock performance graphs, multi-exam consolidated views, and bulk PDF printing. Check the Pricing section above for the full comparison." },
    { q: "How are teacher credentials delivered?", a: "When a school admin adds a teacher, the system auto-generates a username and password and dispatches them via email and SMS through our notification service. No manual sharing is needed." },
    { q: "What if we need help or something breaks?", a: "Contact us via the email in the footer. For technical issues, your super admin can also check the API health endpoint and server logs directly." },
  ];

  return (
    <div className="landing-page">

      {/* ═══════════════════ STICKY NAV ═══════════════════ */}
      <nav className={`landing-nav${navScrolled ? " scrolled" : ""}`}>
        <div className="landing-nav-inner">
          <Link to="/" className="landing-logo">
            <img src="/logo.png" alt="VRM Logo" className="landing-logo-img" />
            <div className="landing-logo-text-block">
              <span className="landing-logo-text">Volcanic Result Management System</span>
              <span className="landing-logo-sub">Volcanic Education and Technology Pvt Ltd</span>
            </div>
          </Link>

          <div className="landing-nav-links">
            <a href="#about"      className="landing-nav-link">About</a>
            <a href="#features"   className="landing-nav-link">Features</a>
            <a href="#how"        className="landing-nav-link">How It Works</a>
            <a href="#pricing"    className="landing-nav-link">Pricing</a>
            <a href="#faq"        className="landing-nav-link">FAQ</a>
            <Link to="/portal"    className="landing-nav-link">Student Portal</Link>
            <Link to="/login"     className="btn secondary sm">Login</Link>
            <Link to="/register"  className="btn sm">Register School</Link>
          </div>
        </div>
      </nav>

      {/* ═══════════════════ HERO ═══════════════════ */}
      <section className="landing-hero">
        <div className="landing-hero-inner">
          <div className="hero-logo-lockup">
            <div className="logo-on-dark" style={{ padding: "8px 10px", borderRadius: 14 }}>
              <img src="/logo.png" alt="Volcanic Education and Technology" className="logo-img-md" />
            </div>
            <div>
              <div className="hero-brand-name">Volcanic Result Management System</div>
              <div className="hero-brand-unit">A unit of Volcanic Education and Technology Pvt Ltd</div>
            </div>
          </div>
          <div className="landing-hero-badge">🇮🇳 Built for Indian Schools</div>
          <h1>India's Smartest School<br />Result Management System</h1>
          <p className="landing-hero-sub">
            Automate marks entry, generate PDF result cards, and publish results to parents —
            all from one secure, multi-school SaaS platform trusted by schools across India.
          </p>
          <div className="landing-hero-btns">
            <Link to="/register" className="landing-hero-btn-primary">
              🚀 Register Your School — Free
            </Link>
            <Link to="/login" className="landing-hero-btn-secondary">
              🔑 Login to Dashboard
            </Link>
          </div>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, marginTop: 20 }}>
            No credit card required to start · Setup in under 5 minutes
          </p>
        </div>

        {/* Mock dashboard preview */}
        <div className="hero-mockup">
          <div className="hero-mockup-bar">
            <span className="mockup-dot red"></span>
            <span className="mockup-dot yellow"></span>
            <span className="mockup-dot green"></span>
            <span style={{ flex: 1, textAlign: "center", fontSize: 12, color: "#94a3b8" }}>
              Volcanic Result Manager — School Admin Dashboard
            </span>
          </div>
          <div className="hero-mockup-body">
            <div className="hero-mockup-sidebar">
              {["📊 Overview", "🎓 Students", "👩‍🏫 Teachers", "📝 Exams", "📄 Results", "📤 Export"].map((item) => (
                <div key={item} className={`mockup-nav-item${item.startsWith("📝") ? " active" : ""}`}>{item}</div>
              ))}
            </div>
            <div className="hero-mockup-content">
              <div className="mockup-title">Exams &amp; Marks Entry Panels</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <div className="mockup-stat">
                  <div className="mockup-stat-val">4</div>
                  <div className="mockup-stat-lbl">Exams</div>
                </div>
                <div className="mockup-stat">
                  <div className="mockup-stat-val">12</div>
                  <div className="mockup-stat-lbl">Panels</div>
                </div>
                <div className="mockup-stat">
                  <div className="mockup-stat-val">340</div>
                  <div className="mockup-stat-lbl">Students</div>
                </div>
              </div>
              <div style={{ background: "#1e293b", borderRadius: 6, overflow: "hidden" }}>
                {[
                  { class: "Class 10 - A", exam: "Unit Test 1", status: "open" },
                  { class: "Class 9 - B",  exam: "Mid-Term",    status: "closed" },
                  { class: "Class 8 - A",  exam: "Annual",      status: "open" },
                ].map((row) => (
                  <div key={row.class} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderBottom: "1px solid #334155" }}>
                    <span style={{ fontSize: 12, color: "#cbd5e1" }}>{row.class}</span>
                    <span style={{ fontSize: 12, color: "#94a3b8" }}>{row.exam}</span>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: row.status === "open" ? "#14532d" : "#450a0a", color: row.status === "open" ? "#86efac" : "#fca5a5" }}>{row.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ STATS BAR ═══════════════════ */}
      <div className="stats-bar">
        <div className="stats-bar-inner">
          {[
            { val: "500+",   lbl: "Schools Onboarded" },
            { val: "2 Lakh+", lbl: "Students Managed" },
            { val: "50 Lakh+", lbl: "Results Published" },
            { val: "99.9%",  lbl: "Uptime SLA" },
          ].map((s) => (
            <div key={s.lbl} className="stats-bar-item">
              <div className="stats-bar-val">{s.val}</div>
              <div className="stats-bar-lbl">{s.lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════════════════ ABOUT ═══════════════════ */}
      <section id="about" className="about-section">
        <div className="landing-section-inner">
          <div className="about-grid">
            <div className="about-text">
              <div className="landing-section-label">About VRM</div>
              <h2 className="landing-section-title">Built by educators, for educators</h2>
              <p style={{ color: "var(--muted)", lineHeight: 1.8, marginBottom: 16 }}>
                <strong>Volcanic Result Manager</strong> was born out of frustration with the old way of managing school results —
                physical register books, Excel sheets emailed back and forth, results printed days after exams,
                and parents calling the school just to know if their child passed.
              </p>
              <p style={{ color: "var(--muted)", lineHeight: 1.8, marginBottom: 16 }}>
                We built a single, secure platform that gives every stakeholder exactly what they need:
                school admins get full control, teachers get a simple marks-entry interface, and
                parents and students get instant access to published results from any device.
              </p>
              <p style={{ color: "var(--muted)", lineHeight: 1.8, marginBottom: 28 }}>
                Our platform is designed for CBSE, ICSE, and State Board schools across India,
                supporting scholastic and co-scholastic subjects, grading systems, and multi-exam
                consolidated result views.
              </p>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <Link to="/register" className="btn">Start Free Registration</Link>
                <Link to="/portal"   className="btn secondary">Try Student Portal</Link>
              </div>
            </div>
            <div className="about-highlights">
              {[
                { icon: "🛡️", title: "CBSE & ICSE Ready",     desc: "Supports scholastic, co-scholastic subjects, and grade-point systems used by all major boards." },
                { icon: "🌐", title: "Cloud-First",            desc: "Access from anywhere — office, classroom, or home — on any browser, any device." },
                { icon: "⚡", title: "Real-Time Updates",      desc: "Results publish instantly. No waiting for batch jobs or overnight processing." },
                { icon: "🔐", title: "Role-Based Security",    desc: "Super Admin, School Admin, Teacher, and Portal roles — each sees only what they should." },
              ].map((h) => (
                <div key={h.title} className="about-highlight-card">
                  <span className="about-highlight-icon">{h.icon}</span>
                  <div>
                    <div className="about-highlight-title">{h.title}</div>
                    <div className="about-highlight-desc">{h.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ FEATURES ═══════════════════ */}
      <section id="features" className="features-section">
        <div className="landing-section-inner">
          <div className="landing-section-center">
            <div className="landing-section-label">Platform Features</div>
            <h2 className="landing-section-title">Everything your school needs</h2>
            <p className="landing-section-sub">
              From marks entry to result publication — VRM handles the entire exam result lifecycle in one place.
            </p>
          </div>
          <div className="features-grid">
            {features.map((f) => (
              <div key={f.title} className="feature-card">
                <div className="feature-icon">{f.icon}</div>
                <div className="feature-title">{f.title}</div>
                <div className="feature-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ WHO IS IT FOR ═══════════════════ */}
      <section className="roles-section">
        <div className="landing-section-inner">
          <div className="landing-section-center">
            <div className="landing-section-label">User Roles</div>
            <h2 className="landing-section-title">One platform, every stakeholder</h2>
            <p className="landing-section-sub">
              VRM is designed for all four key users in a school's result workflow.
            </p>
          </div>
          <div className="roles-grid">
            {userTypes.map((u) => (
              <div key={u.title} className="role-card">
                <div className="role-icon" style={{ background: u.color + "18", color: u.color }}>{u.icon}</div>
                <div className="role-title" style={{ color: u.color }}>{u.title}</div>
                <ul className="role-points">
                  {u.points.map((p) => (
                    <li key={p} className="role-point"><span className="role-check" style={{ color: u.color }}>✓</span>{p}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ HOW IT WORKS ═══════════════════ */}
      <section id="how" className="how-section">
        <div className="landing-section-inner">
          <div className="landing-section-center">
            <div className="landing-section-label">Getting Started</div>
            <h2 className="landing-section-title">Live in under 30 minutes</h2>
            <p className="landing-section-sub">
              Follow these five steps and your school will be publishing results the same day.
            </p>
          </div>
          <div className="how-timeline">
            {[
              { num: "01", icon: "📝", title: "Register Your School", desc: "Fill in your school details and set up the admin account in under 2 minutes. No technical knowledge required." },
              { num: "02", icon: "💳", title: "Choose a Plan & Pay", desc: "Select a subscription plan that fits your school size and pay securely via Razorpay. Activation is instant." },
              { num: "03", icon: "🏫", title: "Set Up Classes & Students", desc: "Add your classes, subjects, and enroll students. Bulk import via Excel is also supported." },
              { num: "04", icon: "👩‍🏫", title: "Add Teachers & Assign Subjects", desc: "Add teachers — credentials are auto-generated and sent by SMS/email. Assign them to subjects and classes." },
              { num: "05", icon: "📊", title: "Open Panels & Enter Marks", desc: "Create an exam and open marks-entry panels with a start and end time. Teachers enter marks only during the window." },
              { num: "06", icon: "🎉", title: "Publish Results", desc: "With one click, publish results for an entire class. Students and parents can view them instantly on the portal." },
            ].map((step, i) => (
              <div key={step.num} className="how-timeline-item">
                <div className="how-timeline-left">
                  <div className="how-num">{step.num}</div>
                  {i < 5 && <div className="how-connector"></div>}
                </div>
                <div className="how-timeline-card">
                  <div className="how-card-icon">{step.icon}</div>
                  <div>
                    <div className="how-card-title">{step.title}</div>
                    <div className="how-card-desc">{step.desc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ TESTIMONIALS ═══════════════════ */}
      <section className="testimonials-section">
        <div className="landing-section-inner">
          <div className="landing-section-center">
            <div className="landing-section-label">Testimonials</div>
            <h2 className="landing-section-title">What school leaders say</h2>
            <p className="landing-section-sub">
              Principals and academic heads share their experience with Volcanic Result Manager.
            </p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((t) => (
              <div key={t.name} className="testimonial-card">
                <div className="testimonial-stars">★★★★★</div>
                <p className="testimonial-quote">"{t.quote}"</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">{t.name[0]}</div>
                  <div>
                    <div className="testimonial-name">{t.name}</div>
                    <div className="testimonial-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ PRICING ═══════════════════ */}
      <section id="pricing" className="pricing-section">
        <div className="landing-section-inner">
          <div className="landing-section-center">
            <div className="landing-section-label">Pricing</div>
            <h2 className="landing-section-title">Simple, transparent plans</h2>
            <p className="landing-section-sub">
              All plans include unlimited students, teachers, classes, and exam sessions.
              Choose the plan that matches your school's needs.
            </p>
          </div>

          {plans.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">💳</div>
              <div className="empty-state-text">Loading plans...</div>
            </div>
          ) : (
            <div className="pricing-grid">
              {plans.map((plan, idx) => {
                const flags = typeof plan.feature_flags === "string"
                  ? JSON.parse(plan.feature_flags)
                  : plan.feature_flags || {};
                return (
                  <div key={plan.id} className={`pricing-card${idx === 1 ? " featured" : ""}`}>
                    {idx === 1 && (
                      <div className="pricing-popular-badge">⭐ Most Popular</div>
                    )}
                    <div className="pricing-plan-name">{plan.name}</div>
                    <div className="pricing-price">₹{Number(plan.price).toLocaleString("en-IN")}</div>
                    <div className="pricing-price-label">per year · one-time payment</div>
                    <div className="pricing-duration">✅ {plan.duration_days} days access</div>
                    <ul className="pricing-features">
                      <li className="pricing-feature"><span className="pricing-feature-check">✓</span>Unlimited students &amp; teachers</li>
                      <li className="pricing-feature"><span className="pricing-feature-check">✓</span>Unlimited classes &amp; subjects</li>
                      <li className="pricing-feature"><span className="pricing-feature-check">✓</span>PDF result cards</li>
                      <li className="pricing-feature"><span className="pricing-feature-check">✓</span>Excel export</li>
                      <li className="pricing-feature"><span className="pricing-feature-check">✓</span>Secure student portal</li>
                      <li className="pricing-feature"><span className="pricing-feature-check">✓</span>Auto SMS/email credentials</li>
                      <li className="pricing-feature"><span className="pricing-feature-check">✓</span>Promotion list generator</li>
                      {flags.graphs && (
                        <li className="pricing-feature"><span className="pricing-feature-check">✓</span>Performance graphs</li>
                      )}
                      {flags.maxExamViews >= 4 && (
                        <li className="pricing-feature"><span className="pricing-feature-check">✓</span>Consolidated multi-exam view (up to 4)</li>
                      )}
                      {flags.bulkPrint && (
                        <li className="pricing-feature"><span className="pricing-feature-check">✓</span>Bulk PDF printing</li>
                      )}
                    </ul>
                    <Link to="/register" className={`btn${idx === 1 ? "" : " secondary"}`} style={{ width: "100%", justifyContent: "center" }}>
                      {idx === 1 ? "🚀 Get Started" : "Choose Plan"}
                    </Link>
                  </div>
                );
              })}
            </div>
          )}

          <div className="pricing-note">
            🔒 Payments are processed securely via Razorpay · 📞 Need a custom plan for a large school group?{" "}
            <a href="mailto:support@volcanicvrm.in">Contact us</a>
          </div>
        </div>
      </section>

      {/* ═══════════════════ FAQ ═══════════════════ */}
      <section id="faq" className="faq-section">
        <div className="landing-section-inner">
          <div className="landing-section-center">
            <div className="landing-section-label">FAQ</div>
            <h2 className="landing-section-title">Frequently asked questions</h2>
            <p className="landing-section-sub">
              Everything you need to know before getting started.
            </p>
          </div>
          <div className="faq-list">
            {faqs.map((f) => (
              <FaqItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ CTA BANNER ═══════════════════ */}
      <section className="cta-section">
        <div className="cta-inner">
          <div className="cta-text">
            <h2 className="cta-title">Ready to modernise your school's results?</h2>
            <p className="cta-sub">
              Join hundreds of schools that have already made the switch.
              Registration takes less than 5 minutes.
            </p>
          </div>
          <div className="cta-actions">
            <Link to="/register" className="landing-hero-btn-primary" style={{ fontSize: 16, padding: "14px 32px" }}>
              🚀 Register Your School
            </Link>
            <Link to="/login" className="cta-login-link">
              Already registered? Login →
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════ FOOTER ═══════════════════ */}
      <footer className="landing-footer-full">
        <div className="landing-footer-grid">
          <div className="footer-brand-col">
            <div className="footer-brand-lockup">
              <div className="logo-on-dark" style={{ padding: "6px 8px", borderRadius: 10 }}>
                <img src="/logo.png" alt="VRM Logo" className="logo-img-sm" />
              </div>
              <div>
                <div className="footer-logo">Volcanic Result Management System</div>
                <div className="footer-unit">A unit of Volcanic Education and Technology Pvt Ltd</div>
              </div>
            </div>
            <p className="footer-tagline">
              India's trusted school result management platform — secure, simple, and built for every school.
            </p>
            <div className="footer-contact">
              <div className="footer-contact-item">📧 support@volcanicvrm.in</div>
              <div className="footer-contact-item">📞 +91 98765 43210</div>
              <div className="footer-contact-item">🏢 Jharkhand, India</div>
            </div>
          </div>

          <div className="footer-links-col">
            <div className="footer-col-title">Platform</div>
            <a href="#features"  className="footer-link">Features</a>
            <a href="#pricing"   className="footer-link">Pricing</a>
            <a href="#how"       className="footer-link">How It Works</a>
            <a href="#faq"       className="footer-link">FAQ</a>
          </div>

          <div className="footer-links-col">
            <div className="footer-col-title">Access</div>
            <Link to="/register" className="footer-link">Register School</Link>
            <Link to="/login"    className="footer-link">School / Admin Login</Link>
            <Link to="/portal"   className="footer-link">Student & Parent Portal</Link>
          </div>

          <div className="footer-links-col">
            <div className="footer-col-title">For Schools</div>
            <a href="#about"     className="footer-link">About VRM</a>
            <Link to="/register" className="footer-link">Get Started Free</Link>
            <a href="mailto:support@volcanicvrm.in" className="footer-link">Contact Support</a>
          </div>
        </div>

        <div className="footer-bottom">
          <div>&copy; {new Date().getFullYear()} Volcanic Education and Technology Pvt Ltd. All rights reserved.</div>
          <div className="footer-bottom-links">
            <a href="#!" className="footer-link-sm">Privacy Policy</a>
            <a href="#!" className="footer-link-sm">Terms of Service</a>
            <a href="#!" className="footer-link-sm">Refund Policy</a>
          </div>
        </div>
      </footer>

    </div>
  );
}
